/**
 * Template inputs form (web): the small dialog a research template opens
 * before it is enabled: a topic (optional, the job preferences fill it), a
 * region, a link to watch, or one of the user's PDFs. Rules are shared with
 * native (`nova-template-inputs.ts`); the server validates again and names
 * the field to fix.
 */
import { useEffect, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import type { NovaTemplateInputSpec } from '@tabbio/types';
import { WebDialog } from '../common/WebDialog.web';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import { SpinnerGlyph } from '../common/SpinnerGlyph.web';
import { listPdfAssets, type NovaPdfAsset } from '../../lib/automation-api';
import {
  emptyTemplateInputs,
  NOVA_TEMPLATE_INPUT_LIMITS,
  validateTemplateInputs,
  type NovaTemplateInputErrors,
  type NovaTemplateInputsFormProps,
  type NovaTemplateInputValues,
} from './nova-template-inputs';

export function NovaTemplateInputsForm({
  template,
  busy,
  serverError,
  onClose,
  onSubmit,
}: NovaTemplateInputsFormProps) {
  const { t } = useTranslation('common');
  const [values, setValues] = useState<NovaTemplateInputValues>(emptyTemplateInputs);
  const [errors, setErrors] = useState<NovaTemplateInputErrors>({});
  const [pdfs, setPdfs] = useState<NovaPdfAsset[] | null>(null);
  const [pdfsFailed, setPdfsFailed] = useState(false);
  const specs: NovaTemplateInputSpec[] = template?.inputs ?? [];
  const wantsPdf = specs.some((spec) => spec.kind === 'pdf_asset');
  const templateKey = template?.key ?? null;

  // A fresh form for each template.
  useEffect(() => {
    setValues(emptyTemplateInputs());
    setErrors({});
  }, [templateKey]);

  useEffect(() => {
    if (!templateKey || !wantsPdf) return;
    let cancelled = false;
    setPdfs(null);
    setPdfsFailed(false);
    listPdfAssets()
      .then((rows) => {
        if (!cancelled) setPdfs(rows);
      })
      .catch(() => {
        if (!cancelled) setPdfsFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [templateKey, wantsPdf]);

  if (!template) return null;

  const set = (key: keyof NovaTemplateInputValues, value: string) => {
    setValues((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: undefined }));
  };

  const submit = () => {
    const found = validateTemplateInputs(specs, values);
    setErrors(found);
    if (Object.values(found).some(Boolean)) return;
    onSubmit(values);
  };

  const errorFor = (key: keyof NovaTemplateInputValues) =>
    serverError?.field === key
      ? serverError.message
      : errors[key]
        ? t(errors[key]!)
        : null;

  return (
    <WebDialog
      title={t('nova.templateInputs.title', { name: template.title })}
      subtitle={template.description ?? undefined}
      onClose={onClose}
      maxWidth={520}
      scrollable
      footer={
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            type="button"
            className="tabbio-chip"
            onClick={onClose}
            disabled={busy}
            style={{ height: 48, paddingInline: 20 }}
          >
            {t('nova.templateInputs.cancel')}
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            style={primaryBtn(busy)}
          >
            {busy ? t('nova.templateInputs.submitting') : t('nova.templateInputs.submit')}
          </button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18, paddingBottom: 8 }}>
        <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5, color: 'var(--text-secondary)' }}>
          {t('nova.templateInputs.subtitle')}
        </p>

        {specs.map((spec) => {
          if (spec.kind === 'pdf_asset') {
            return (
              <div key={spec.key} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <span style={fieldLabel}>{t('nova.templateInputs.pdfLabel')}</span>
                {pdfsFailed ? (
                  <span style={hint}>{t('nova.templateInputs.pdfLoadFailed')}</span>
                ) : pdfs === null ? (
                  <span style={{ ...hint, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <SpinnerGlyph color="#5e29ff" size={14} />
                    {t('nova.templateInputs.pdfLoading')}
                  </span>
                ) : pdfs.length === 0 ? (
                  <span style={hint}>{t('nova.templateInputs.pdfEmpty')}</span>
                ) : (
                  <div role="radiogroup" aria-label={t('nova.templateInputs.pdfLabel')} style={list}>
                    {pdfs.map((pdf, index) => {
                      const checked = values.asset_id === pdf.id;
                      return (
                        <button
                          key={pdf.id}
                          type="button"
                          role="radio"
                          aria-checked={checked}
                          onClick={() => set('asset_id', pdf.id)}
                          style={{
                            ...pdfRow,
                            borderTop: index === 0 ? 'none' : '1px solid var(--input-border)',
                          }}
                        >
                          <MaterialSymbol
                            name="picture_as_pdf"
                            size={18}
                            style={{ color: 'var(--text-tertiary)', flexShrink: 0 }}
                          />
                          <span dir="auto" style={pdfTitle}>
                            {pdf.title}
                          </span>
                          <MaterialSymbol
                            name={checked ? 'radio_button_checked' : 'radio_button_unchecked'}
                            size={18}
                            style={{
                              color: checked ? 'var(--text-primary)' : 'var(--text-tertiary)',
                              flexShrink: 0,
                            }}
                          />
                        </button>
                      );
                    })}
                  </div>
                )}
                <FieldError message={errorFor('asset_id')} />
              </div>
            );
          }
          const isUrl = spec.kind === 'url';
          const label =
            spec.key === 'topic'
              ? t('nova.templateInputs.topicLabel')
              : spec.key === 'region'
                ? t('nova.templateInputs.regionLabel')
                : t('nova.templateInputs.urlLabel');
          const placeholder =
            spec.key === 'topic'
              ? t('nova.templateInputs.topicPlaceholder')
              : spec.key === 'region'
                ? t('nova.templateInputs.regionPlaceholder')
                : t('nova.templateInputs.urlPlaceholder');
          const help =
            spec.key === 'topic' && !spec.required
              ? t('nova.templateInputs.topicHint')
              : isUrl
                ? t('nova.templateInputs.urlHint')
                : null;
          return (
            <label key={spec.key} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={fieldLabel}>{label}</span>
              <input
                type={isUrl ? 'url' : 'text'}
                value={values[spec.key]}
                onChange={(event) => set(spec.key, event.target.value)}
                placeholder={placeholder}
                maxLength={NOVA_TEMPLATE_INPUT_LIMITS[spec.key]}
                dir={isUrl ? 'ltr' : 'auto'}
                autoComplete="off"
                style={input}
              />
              {help ? <span style={hint}>{help}</span> : null}
              <FieldError message={errorFor(spec.key)} />
            </label>
          );
        })}
      </div>
    </WebDialog>
  );
}

function FieldError({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <span role="alert" dir="auto" style={{ fontSize: 12.5, color: '#b26a00' }}>
      {message}
    </span>
  );
}

const fieldLabel: CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--text-primary)',
};

const hint: CSSProperties = { fontSize: 12, color: 'var(--text-tertiary)' };

const input: CSSProperties = {
  width: '100%',
  height: 46,
  borderRadius: 12,
  border: '1px solid var(--input-border)',
  background: 'var(--menu-bg)',
  color: 'var(--text-primary)',
  fontSize: 14,
  paddingInline: 14,
  outline: 'none',
  fontFamily: 'inherit',
};

const list: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 12,
  border: '1px solid var(--input-border)',
  overflow: 'hidden',
};

const pdfRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '11px 14px',
  border: 0,
  background: 'transparent',
  cursor: 'pointer',
  textAlign: 'start',
  fontFamily: 'inherit',
  width: '100%',
};

const pdfTitle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  fontSize: 14,
  color: 'var(--text-primary)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

function primaryBtn(disabled: boolean): CSSProperties {
  return {
    flex: 1,
    width: '100%',
    height: 48,
    paddingInline: 24,
    borderRadius: 9999,
    border: 0,
    background: 'var(--text-primary)',
    color: 'var(--bg)',
    fontSize: 15,
    fontWeight: 600,
    cursor: disabled ? 'default' : 'pointer',
    opacity: disabled ? 0.7 : 1,
    fontFamily: 'inherit',
    transition: 'opacity 150ms',
  };
}
